// Code highlighting (13-messages.md, "Code blocks"): Prism, loaded the first time a code block is
// shown, with the fixed set of languages people paste at work (the full set was a candidate to drop).
import type PrismType from 'prismjs';
import { useEffect, useState } from 'react';

const ALIASES: Record<string, string> = { rs: 'rust', js: 'javascript', ts: 'typescript', sh: 'bash', shell: 'bash', yml: 'yaml', html: 'markup', xml: 'markup', py: 'python', rb: 'ruby', kt: 'kotlin', cs: 'csharp', md: 'markdown', dockerfile: 'docker' };

let loading: Promise<typeof PrismType> | undefined;

/** Prism with its languages, each after the one it extends; a failed load is tried again next time. */
function prism(): Promise<typeof PrismType> {
  loading ??= (async () => {
    const Prism = (await import('prismjs')).default;
    (globalThis as { Prism?: typeof PrismType }).Prism = Prism;
    await Promise.all([import('prismjs/components/prism-c'), import('prismjs/components/prism-json'), import('prismjs/components/prism-bash'), import('prismjs/components/prism-markdown'), import('prismjs/components/prism-markup-templating')]);
    await Promise.all([
      import('prismjs/components/prism-cpp'),
      import('prismjs/components/prism-csharp'),
      import('prismjs/components/prism-diff'),
      import('prismjs/components/prism-docker'),
      import('prismjs/components/prism-go'),
      import('prismjs/components/prism-java'),
      import('prismjs/components/prism-kotlin'),
      import('prismjs/components/prism-php'),
      import('prismjs/components/prism-python'),
      import('prismjs/components/prism-ruby'),
      import('prismjs/components/prism-rust'),
      import('prismjs/components/prism-sql'),
      import('prismjs/components/prism-swift'),
      import('prismjs/components/prism-toml'),
      import('prismjs/components/prism-typescript'),
      import('prismjs/components/prism-yaml'),
    ]);
    return Prism;
  })().catch((e) => {
    loading = undefined;
    throw e;
  });
  return loading;
}

/** The language a "language-…" class names, as Prism calls it. */
export const languageOf = (className: string | null) => {
  const name = /(?:^|\s)language-([\w+#-]+)/.exec(className ?? '')?.[1]?.toLowerCase();
  return name && (ALIASES[name] ?? name);
};

/** `code` highlighted as `language` once Prism has loaded; undefined until then or without a language. */
export function useHighlight(code: string, language: string | undefined) {
  const [html, setHtml] = useState<string>();
  useEffect(() => {
    if (!language) return;
    let live = true;
    prism().then((Prism) => live && Prism.languages[language] && setHtml(Prism.highlight(code, Prism.languages[language], language)), () => {});
    return () => void (live = false);
  }, [code, language]);
  return html;
}
